/**
 * Sentry service wrapper.
 *
 * All methods are no-ops when VITE_SENTRY_ENABLED !== 'true' so the rest of the
 * application never needs to check the flag — just import and call.
 *
 * The rest of the app must NEVER import @sentry/vue directly; go through this
 * service instead so the integration can be swapped or disabled cleanly.
 */
import type { App } from 'vue'
import * as Sentry from '@sentry/vue'
import type { ClassifiedError, ErrorKind } from '@/utils/errors'
import router from '@/router'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const isEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'
const dsn: string = import.meta.env.VITE_SENTRY_DSN as string ?? ''
const environment: string = (import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE

/** Map each ErrorKind to the appropriate Sentry severity level. */
const SENTRY_SEVERITY: Record<ErrorKind, Sentry.SeverityLevel> = {
  network: 'warning',
  auth: 'warning',
  validation: 'info',
  business: 'warning',
  server: 'error',
  unknown: 'error',
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const sentryService = {
  /**
   * Initialize Sentry.  Must be called once in main.ts, after all app.use()
   * calls and before app.mount().
   */
  init(app: App): void {
    if (!isEnabled || !dsn) return

    Sentry.init({
      app,
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      tracePropagationTargets: [/\/api\/v1\//],
      // Pass Vue Router so Sentry can record navigation as transactions
      // @ts-expect-error – Sentry accepts the router via init options when using browserTracingIntegration
      router,
      // Stale JS chunks after a new deployment cause "error loading dynamically
      // imported module" TypeErrors.  These are handled by the unhandledrejection
      // listener in main.ts (cache purge + hard reload) and must not be reported
      // as bugs in Sentry.
      ignoreErrors: [
        /dynamically imported module/i,
        /Failed to fetch dynamically imported module/i,
        /Importing a module script failed/i,
      ],
    })
  },

  /** Capture a ClassifiedError and attach optional structured context. */
  captureError(classified: ClassifiedError, context?: Record<string, unknown>): void {
    if (!isEnabled) return

    const level = SENTRY_SEVERITY[classified.kind]

    Sentry.withScope((scope) => {
      scope.setLevel(level)
      scope.setTag('error_kind', classified.kind)
      if (classified.statusCode !== undefined) {
        scope.setTag('http_status', String(classified.statusCode))
      }
      if (context) {
        scope.setContext('api_context', context)
      }

      const originalError = classified.originalError
      if (originalError instanceof Error) {
        Sentry.captureException(originalError)
      } else {
        Sentry.captureMessage(classified.technicalMessage, level)
      }
    })
  },

  /** Set authenticated user context so Sentry links events to a user. */
  setUser(user: { id: string; email: string; orgSlug: string } | null): void {
    if (!isEnabled) return

    if (user === null) {
      Sentry.setUser(null)
    } else {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.orgSlug,
      })
    }
  },

  /** Add a breadcrumb (navigation events, API calls, etc.). */
  addBreadcrumb(message: string, category: string, data?: Record<string, unknown>): void {
    if (!isEnabled) return

    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    })
  },
}
